import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { steamProfileUrl } from "../utils/steam";

const API = process.env.REACT_APP_API_URL || "";

const SORTS = [
  { value: "rating", label: "Rating" },
  { value: "kd",     label: "K/D" },
  { value: "adr",    label: "ADR" },
  { value: "kills",  label: "Kills" },
];

function StandingsTable({ standings }) {
  if (!standings?.length) return <p style={{ color: "#9ca3af" }}>No matches played yet.</p>;
  const max = standings[0]?.points || 1;

  return (
    <table className="table">
      <thead>
        <tr>
          <th>#</th>
          <th>Team</th>
          <th>MP</th>
          <th>W</th>
          <th>D</th>
          <th>L</th>
          <th>RW</th>
          <th>RL</th>
          <th>RD</th>
          <th>Pts</th>
        </tr>
      </thead>
      <tbody>
        {standings.map((s, i) => {
          const rd = s.rounds_won - s.rounds_lost;
          return (
            <tr key={s.team_name}>
              <td style={{ color: i < 3 ? "#f0a500" : "#6b7280" }}>{i + 1}</td>
              <td style={{ fontWeight: "600" }}>{s.team_name}</td>
              <td style={{ color: "#9ca3af" }}>{s.matches_played}</td>
              <td style={{ color: "#4ade80" }}>{s.wins}</td>
              <td style={{ color: "#9ca3af" }}>{s.draws}</td>
              <td style={{ color: "#ef4444" }}>{s.losses}</td>
              <td>{s.rounds_won}</td>
              <td>{s.rounds_lost}</td>
              <td style={{ color: rd > 0 ? "#4ade80" : rd < 0 ? "#ef4444" : "#9ca3af" }}>
                {rd > 0 ? "+" : ""}{rd}
              </td>
              <td>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <div style={{ flex: 1, background: "#2d3148", borderRadius: "3px", height: "6px", minWidth: "40px" }}>
                    <div style={{ width: `${(s.points / max) * 100}%`, background: "#f0a500", height: "100%", borderRadius: "3px" }} />
                  </div>
                  <span style={{ fontWeight: "700", color: "#f0a500", minWidth: "20px" }}>{s.points}</span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function MatchHistory({ matches }) {
  if (!matches?.length) return <p style={{ color: "#9ca3af" }}>No matches yet.</p>;
  return (
    <table className="table">
      <thead>
        <tr><th>Map</th><th>Teams</th><th>Score</th><th>Date</th><th></th></tr>
      </thead>
      <tbody>
        {matches.map(m => (
          <tr key={m.id}>
            <td style={{ fontWeight: "600" }}>{m.map}</td>
            <td style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
              {m.team1_name} vs {m.team2_name}
            </td>
            <td style={{ color: "#f0a500", fontWeight: "700" }}>
              {m.team1_score} – {m.team2_score}
            </td>
            <td style={{ color: "#6b7280", fontSize: "0.8rem" }}>
              {m.finished_at ? new Date(m.finished_at).toLocaleDateString() : "—"}
            </td>
            <td>
              <Link to={`/matches/${m.id}`} style={{ color: "#5865f2", fontSize: "0.8rem" }}>View →</Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TopPlayers({ players }) {
  if (!players?.length) return <p style={{ color: "#9ca3af" }}>No player data yet.</p>;
  const medals = ["🥇","🥈","🥉"];
  return (
    <table className="table">
      <thead>
        <tr><th>#</th><th>Player</th><th>Matches</th><th>K/D</th><th>ADR</th><th>Rating</th></tr>
      </thead>
      <tbody>
        {players.map((p, i) => (
          <tr key={p.steam_id}>
            <td>{medals[i] || i + 1}</td>
            <td>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {p.avatar_url && <img src={p.avatar_url} alt="" style={{ width: 24, height: 24, borderRadius: "50%" }} />}
                <Link to={`/players/${p.steam_id}`}>{p.name}</Link>
                {steamProfileUrl(p) && (
                  <a href={steamProfileUrl(p)} target="_blank" rel="noopener noreferrer"
                    style={{ color: "#4a9eff", fontSize: "0.75rem", opacity: 0.7 }}>↗</a>
                )}
              </div>
            </td>
            <td>{p.matches}</td>
            <td>{p.kd}</td>
            <td>{p.avg_adr}</td>
            <td style={{ color: "#f0a500", fontWeight: "600" }}>{p.avg_rating}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function SeasonDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("standings");
  const [sort, setSort] = useState("rating");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/seasons/${id}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (tab !== "leaderboard") return;
    axios.get(`${API}/api/seasons/${id}/leaderboard?sort=${sort}`)
      .then(r => setLeaderboard(r.data.leaderboard || []))
      .catch(console.error);
  }, [id, tab, sort]);

  const btnStyle = (active) => ({
    padding: "0.4rem 1.1rem", borderRadius: "6px", border: "none",
    cursor: "pointer", fontWeight: active ? "700" : "400",
    background: active ? "#f0a500" : "#2d3148",
    color: active ? "#0f1117" : "#e5e7eb",
  });

  if (loading) return <div className="card" style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>Loading...</div>;
  if (!data) return <div style={{ color: "#ef4444" }}>Season not found.</div>;

  const { season, standings, matches, top_players } = data;
  const STATUS_COLOR = { active: "#4ade80", upcoming: "#fbbf24", finished: "#60a5fa" };

  return (
    <div>
      <div style={{ marginBottom: "1rem" }}>
        <Link to="/seasons" style={{ color: "#9ca3af", fontSize: "0.9rem" }}>← Back to Seasons</Link>
      </div>

      {/* Header */}
      <div className="card" style={{ borderTop: `3px solid ${STATUS_COLOR[season.status] || "#f0a500"}`, marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h1 style={{ marginBottom: "0.3rem" }}>{season.name}</h1>
            {season.description && <p style={{ color: "#9ca3af", fontSize: "0.9rem" }}>{season.description}</p>}
            <div style={{ display: "flex", gap: "1.5rem", marginTop: "0.75rem", fontSize: "0.85rem", color: "#6b7280" }}>
              <span>🏟️ {matches?.length || 0} matches</span>
              <span>👥 {top_players?.length || 0}+ players</span>
              {season.started_at && <span>📅 Started {new Date(season.started_at).toLocaleDateString()}</span>}
              {season.finished_at && <span>🏁 Ended {new Date(season.finished_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <span style={{
            background: season.status === "active" ? "#166534" : season.status === "upcoming" ? "#3b2f0e" : "#1e3a5f",
            color: STATUS_COLOR[season.status], padding: "0.25rem 0.75rem",
            borderRadius: "4px", fontSize: "0.75rem", fontWeight: "700",
          }}>
            {season.status.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Summary stats */}
      <div className="stat-grid" style={{ marginBottom: "1.5rem" }}>
        <div className="stat-box">
          <div className="value">{standings?.length || 0}</div>
          <div className="label">Teams</div>
        </div>
        <div className="stat-box">
          <div className="value">{matches?.length || 0}</div>
          <div className="label">Matches Played</div>
        </div>
        <div className="stat-box">
          <div className="value">{standings?.[0]?.team_name || "—"}</div>
          <div className="label">Current Leader</div>
        </div>
        <div className="stat-box">
          <div className="value">{standings?.[0]?.points ?? "—"}</div>
          <div className="label">Leader Points</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <button style={btnStyle(tab === "standings")} onClick={() => setTab("standings")}>📊 Standings</button>
        <button style={btnStyle(tab === "matches")} onClick={() => setTab("matches")}>🎮 Matches</button>
        <button style={btnStyle(tab === "top")} onClick={() => setTab("top")}>⭐ Top Players</button>
        <button style={btnStyle(tab === "leaderboard")} onClick={() => setTab("leaderboard")}>🏆 Full Leaderboard</button>
      </div>

      {tab === "standings" && (
        <div className="card">
          <h2 style={{ marginBottom: "1rem" }}>Team Standings</h2>
          <StandingsTable standings={standings} />
          <div style={{ marginTop: "1rem", fontSize: "0.75rem", color: "#6b7280" }}>
            Pts = Points (Win: 3, Draw: 1, Loss: 0) · RD = Round Difference
          </div>
        </div>
      )}

      {tab === "matches" && (
        <div className="card">
          <h2 style={{ marginBottom: "1rem" }}>Match History</h2>
          <MatchHistory matches={matches} />
        </div>
      )}

      {tab === "top" && (
        <div className="card">
          <h2 style={{ marginBottom: "1rem" }}>Top 10 Players</h2>
          <TopPlayers players={top_players} />
        </div>
      )}

      {tab === "leaderboard" && (
        <div className="card">
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            {SORTS.map(s => (
              <button key={s.value} style={btnStyle(sort === s.value)} onClick={() => setSort(s.value)}>
                {s.label}
              </button>
            ))}
          </div>
          {leaderboard.length === 0 ? (
            <p style={{ color: "#9ca3af" }}>No data yet.</p>
          ) : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Player</th><th>MP</th><th>K/D</th><th>ADR</th><th>KAST</th><th>Rating</th><th>Aces</th><th>Clutches</th></tr>
              </thead>
              <tbody>
                {leaderboard.map((p, i) => (
                  <tr key={p.steam_id}>
                    <td style={{ color: i < 3 ? "#f0a500" : "#6b7280" }}>
                      {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        {p.avatar_url && <img src={p.avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: "50%" }} />}
                        <Link to={`/players/${p.steam_id}`}>{p.name}</Link>
                      </div>
                    </td>
                    <td style={{ color: "#9ca3af" }}>{p.matches}</td>
                    <td>{p.kd}</td>
                    <td>{p.avg_adr}</td>
                    <td>{p.avg_kast}%</td>
                    <td style={{ color: "#f0a500", fontWeight: "600" }}>{p.avg_rating}</td>
                    <td style={{ color: p.aces > 0 ? "#ef4444" : "inherit" }}>{p.aces}</td>
                    <td style={{ color: p.clutches > 0 ? "#f0a500" : "inherit" }}>{p.clutches}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
